import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { Typography } from '@euler/app/components/typography';
import { AiCampaignMonitor } from '@euler/app/flows/ai-suite/chat/AiCampaignMonitor';
import { openAiChat } from '@euler/app/flows/ai-suite/openAiChat';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { FeatureNotEnabledView } from '@euler/components/entitlements/FeatureNotEnabledView';
import {
  apdmFunnelAttempted,
  apdmFunnelSteps,
} from '@euler/functions/apdm/campaignFunnel';
import { useStoreFeature } from '@euler/functions/entitlements/useStoreFeature';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useUserProfileInfo } from '@euler/functions/user/useUserProfileInfo';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { AiAgentBundleId } from '@euler/model/ai-home';
import {
  ApdmCampaignDetailResult,
  ApdmCampaignKind,
  ApdmCampaignLeadsResult,
  ApdmCampaignMonitor,
  ApdmCampaignRecipientView,
  ApdmCampaignReport,
  ApdmCampaignStatus,
  ApdmCouponCampaignSummary,
  ApdmDraftReviewScope,
  ApdmDraftStatus,
  ApdmReplyDraftReviewSummary,
} from '@euler/model/apdm';
import { StoreFeature } from '@euler/model/enum';
import { useServiceFactory } from '@euler/services/factory';
import { useCreation } from '@euler/utils/hooks';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { RefreshCw } from 'lucide-react-native';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Subject } from 'rxjs';
import { ApdmFunnelChart } from '../components/ApdmFunnelChart';
import { ApdmCard, ApdmSectionTitle } from '../components/ApdmSurface';
import { AnalysisTab } from './AnalysisTab';
import { ContentTab } from './ContentTab';
import { ConversationSheet } from './ConversationSheet';
import { CustomersTab } from './CustomersTab';
import { CampaignControl, DetailHeader } from './DetailHeader';
import { LeadsTab } from './LeadsTab';
import { ReviewsTab } from './ReviewsTab';

export interface ApdmCampaignDetailParams {
  campaignId?: string;
  /** Opens the customer list on those the messages did not reach. */
  filter?: 'undelivered';
  /** The tab to open on; the customers by default. */
  tab?: ApdmCampaignDetailTab;
}

export type ApdmCampaignDetailTab =
  | 'customers'
  | 'leads'
  | 'reviews'
  | 'content'
  | 'analysis';
type Tab = ApdmCampaignDetailTab;

const kWideFrom = 768;
const kDraftsShown = 50;
// How far before the end the customer list starts reading its next page.
const kLoadAheadPx = 400;

interface Loaded {
  detail: ApdmCampaignDetailResult;
  report: ApdmCampaignReport | null;
}

/** The chat's live card, from what this page has read already. */
function campaignMonitor(
  detail: ApdmCampaignDetailResult,
  report: ApdmCampaignReport,
  leads: number,
): ApdmCampaignMonitor {
  const { funnel } = report;
  return {
    campaignId: detail.summary.id,
    title: detail.summary.title,
    status: detail.summary.status,
    audience: funnel.audience,
    attempted: apdmFunnelAttempted(funnel),
    reached: funnel.reached,
    unreachable: funnel.unreachable,
    declined: funnel.doNotContact,
    replied: funnel.replied,
    leads,
    nextSendAt: detail.summary.nextSendAt,
    // The page does not count what is queued; the chat's monitor does.
    queued: 0,
    liveSendingAccounts: detail.liveSendingAccounts,
  };
}

function CampaignDetail({
  storeId,
  campaignId,
  filter,
  initialTab,
}: {
  storeId: number;
  campaignId: string;
  filter?: 'undelivered';
  initialTab?: Tab;
}) {
  const { rpx } = useResponsiveContext();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const wide = width >= kWideFrom;
  const navigation = useAppNavigation();
  const local = useNavigation();
  const confirm = useConfirm();
  const { apdmCampaignService, apdmReviewService, aiAssistantService } =
    useServiceFactory();
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [failed, setFailed] = useState(false);
  const [leads, setLeads] = useState<ApdmCampaignLeadsResult | null>(null);
  const [leadsFailed, setLeadsFailed] = useState(false);
  const [drafts, setDrafts] = useState<ApdmReplyDraftReviewSummary[] | null>(
    null,
  );
  const [draftsFailed, setDraftsFailed] = useState(false);
  const [coupon, setCoupon] = useState<ApdmCouponCampaignSummary | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>(initialTab ?? 'customers');
  const [open, setOpen] = useState<ApdmCampaignRecipientView | null>(null);
  const [working, setWorking] = useState<CampaignControl | null>(null);
  const [reload, setReload] = useState(0);
  const refresh = useCallback(() => setReload(value => value + 1), []);
  // Where the page is scrolled; near the end it tells the customer list
  // to read its next page before the reader gets there.
  const scroll = useRef({ offset: 0, height: 0, content: 0 });
  const endReached$ = useCreation(() => new Subject<void>(), []);
  const measureEnd = () => {
    const { offset, height, content } = scroll.current;
    if (height > 0 && offset + height >= content - kLoadAheadPx)
      endReached$.next();
  };

  // Read again on every return: a reply reviewed in the inbox or a lead
  // worked on its page changes these numbers.
  const load = useCallback(() => {
    let active = true;
    setFailed(false);
    setLeadsFailed(false);
    setDraftsFailed(false);
    Promise.all([
      apdmCampaignService.detail({ campaignId }),
      apdmCampaignService.report({ campaignId }).catch(error => {
        console.error('APDM campaign report failed', error);
        return null;
      }),
    ])
      .then(([detail, report]) => {
        if (!active) return;
        setLoaded({ detail, report });
        if (detail.scope === 'full' && detail.draft.couponCampaignId)
          apdmCampaignService
            .coupon({ campaignId })
            .then(value => active && setCoupon(value))
            .catch(error => console.error('APDM coupon failed', error));
      })
      .catch(error => {
        console.error('APDM campaign detail failed', error);
        if (active) setFailed(true);
      });
    apdmCampaignService
      .leads(campaignId)
      .then(result => {
        if (active) setLeads(result);
      })
      .catch(error => {
        console.error('APDM campaign leads failed', error);
        if (active) setLeadsFailed(true);
      });
    apdmReviewService
      .list({
        scope: ApdmDraftReviewScope.Campaign,
        campaignId,
        afterDraftId: null,
        status: ApdmDraftStatus.Pending,
        limit: kDraftsShown,
      })
      .then(result => {
        if (active) setDrafts(result.rows);
      })
      .catch(error => {
        console.error('APDM campaign drafts failed', error);
        if (active) setDraftsFailed(true);
      });
    aiAssistantService
      .getCampaignSession(campaignId)
      .then(result => {
        if (active) setSessionId(result.sessionId);
      })
      .catch(error => console.error('APDM campaign session failed', error));
    return () => {
      active = false;
    };
  }, [apdmCampaignService, apdmReviewService, aiAssistantService, campaignId]);
  useFocusEffect(load);
  // A reload after an action on this page, when the page keeps its focus.
  useEffect(() => {
    if (reload) return load();
  }, [load, reload]);

  const openConversation = () => {
    if (sessionId)
      openAiChat(navigation, {
        kind: 'session',
        sessionId,
        bundleId: AiAgentBundleId.Apdm,
      });
  };
  const openLead = (id: string) => {
    setOpen(null);
    navigation.navigate('_lead', { id });
  };
  const control = async (action: CampaignControl) => {
    if (!loaded) return;
    if (
      action === 'stop' &&
      !(await confirm({
        title: '停止活动？',
        message:
          '停止后活动结束，不再发送，也不能再继续。已发出的消息和线索都会保留。',
        confirmButtonText: '确认停止',
        destructive: true,
      }))
    )
      return;
    setWorking(action);
    const ref = {
      campaignId,
      expectedVersion: loaded.detail.summary.version,
    };
    try {
      if (action === 'pause') await apdmCampaignService.pause(ref);
      else if (action === 'resume') await apdmCampaignService.resume(ref);
      else await apdmCampaignService.end(ref);
      refresh();
    } catch (error) {
      console.error('APDM campaign control failed', error);
      await confirm({
        title: '没有操作成功',
        message: '活动可能刚刚变过，刷新后再试。',
        cancelButtonDisabled: true,
      });
    } finally {
      setWorking(null);
    }
  };

  if (!loaded)
    return (
      <View
        style={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: aiColors.background,
        }}
      >
        {failed ? (
          <Pressable
            accessibilityRole="button"
            onPress={refresh}
            style={{ flexDirection: 'row', gap: rpx(8), alignItems: 'center' }}
          >
            <RefreshCw size={16} color={aiColors.mutedForeground} />
            <Typography.Text size={rpx(13)} color={aiColors.mutedForeground}>
              暂时无法加载，点击重试
            </Typography.Text>
          </Pressable>
        ) : (
          <ActivityIndicator color={aiColors.primary} />
        )}
      </View>
    );

  const { detail, report } = loaded;
  const monitor =
    report && leads ? campaignMonitor(detail, report, leads.total) : null;
  const definition = detail.scope === 'full' ? detail.draft : null;
  const steps = report
    ? apdmFunnelSteps(report.funnel, {
        greeting: detail.summary.kind === ApdmCampaignKind.Greeting,
        redeemed: !!definition?.couponCampaignId || report.funnel.redeemed > 0,
      })
    : null;
  // The badge counts what the caller can act on; the tab lists all.
  const reviews = drafts?.filter(draft => draft.canReview).length ?? 0;
  const tabs: Array<{ value: Tab; label: string; count?: number }> = [
    { value: 'customers', label: '客户' },
    { value: 'leads', label: '线索', count: leads?.total },
    { value: 'reviews', label: '待审核', count: reviews },
    { value: 'content', label: '内容' },
    { value: 'analysis', label: '分析' },
  ];

  const overview = (
    <View style={{ gap: rpx(16) }}>
      {monitor ? <AiCampaignMonitor campaign={monitor} /> : null}
      {steps ? (
        <ApdmCard style={{ padding: rpx(16), gap: rpx(16) }}>
          <ApdmSectionTitle title="转化漏斗" note="占已触达的比例" />
          <ApdmFunnelChart steps={steps} />
        </ApdmCard>
      ) : null}
    </View>
  );
  const tabBar = (
    <View
      style={{
        flexDirection: 'row',
        gap: rpx(20),
        backgroundColor: aiColors.background,
        borderBottomWidth: 1,
        borderBottomColor: aiColors.border,
      }}
    >
      {tabs.map(item => {
        const active = item.value === tab;
        return (
          <Pressable
            key={item.value}
            accessibilityRole="tab"
            accessibilityState={{ selected: active }}
            onPress={() => setTab(item.value)}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: rpx(4),
              paddingVertical: rpx(10),
              borderBottomWidth: 2,
              borderBottomColor: active ? aiColors.primary : 'transparent',
              marginBottom: -1,
            }}
          >
            <Typography.Text
              {...(active ? { medium: true as const } : {})}
              size={rpx(14)}
              color={active ? aiColors.primary : aiColors.mutedForeground}
            >
              {item.label}
            </Typography.Text>
            {item.count ? (
              item.value === 'reviews' ? (
                <View
                  style={{
                    paddingHorizontal: rpx(6),
                    borderRadius: rpx(8),
                    backgroundColor: aiColors.destructive,
                  }}
                >
                  <Typography.Text bold size={rpx(10)} color="#fff">
                    {item.count}
                  </Typography.Text>
                </View>
              ) : (
                <Typography.Text
                  size={rpx(12)}
                  color={aiColors.mutedForeground}
                >
                  {item.count}
                </Typography.Text>
              )
            ) : null}
          </Pressable>
        );
      })}
    </View>
  );
  const body =
    tab === 'customers' ? (
      <CustomersTab
        campaignId={campaignId}
        version={detail.summary.version}
        initialFilter={filter ?? 'all'}
        onOpen={setOpen}
        onLead={openLead}
        onChanged={refresh}
        endReached$={endReached$}
      />
    ) : tab === 'leads' ? (
      <LeadsTab
        leads={leads}
        failed={leadsFailed}
        onLead={openLead}
        onRetry={refresh}
      />
    ) : tab === 'reviews' ? (
      <ReviewsTab
        storeId={storeId}
        drafts={drafts}
        failed={draftsFailed}
        onDone={refresh}
      />
    ) : tab === 'content' ? (
      <ContentTab
        definition={definition}
        report={report}
        coupon={coupon}
        audience={report?.funnel.audience ?? detail.counts.total}
        onEdit={sessionId ? openConversation : null}
      />
    ) : (
      <AnalysisTab
        campaignId={campaignId}
        report={report}
        ended={detail.summary.status === ApdmCampaignStatus.Ended}
      />
    );

  return (
    <View style={{ flex: 1, backgroundColor: aiColors.background }}>
      <DetailHeader
        detail={detail}
        sessionId={sessionId}
        working={working}
        onBack={() => local.goBack()}
        onConversation={openConversation}
        onControl={action => void control(action)}
      />
      <ScrollView
        stickyHeaderIndices={wide ? undefined : [1]}
        scrollEventThrottle={100}
        onScroll={event => {
          scroll.current.offset = event.nativeEvent.contentOffset.y;
          measureEnd();
        }}
        onLayout={event => {
          scroll.current.height = event.nativeEvent.layout.height;
          measureEnd();
        }}
        onContentSizeChange={(_, height) => {
          scroll.current.content = height;
          measureEnd();
        }}
        contentContainerStyle={{
          padding: rpx(16),
          paddingBottom: insets.bottom + rpx(32),
        }}
      >
        {wide ? (
          <View
            style={{
              width: '100%',
              maxWidth: 1120,
              alignSelf: 'center',
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: rpx(24),
            }}
          >
            <View style={{ flex: 2 }}>{overview}</View>
            <View style={{ flex: 3, gap: rpx(12) }}>
              {tabBar}
              {body}
            </View>
          </View>
        ) : (
          overview
        )}
        {wide ? null : (
          // Sticky on a phone: the bar's own background covers the full
          // width and the gap above it, so rows scrolling under it never
          // show through.
          <View
            style={{
              paddingTop: rpx(16),
              marginHorizontal: -rpx(16),
              paddingHorizontal: rpx(16),
              backgroundColor: aiColors.background,
            }}
          >
            {tabBar}
          </View>
        )}
        {wide ? null : <View style={{ marginTop: rpx(12) }}>{body}</View>}
        {detail.scope === 'assigned' ? (
          <Typography.Text
            size={rpx(12)}
            color={aiColors.mutedForeground}
            style={{ marginTop: rpx(16), textAlign: 'center' }}
          >
            仅显示分配给我的客户
          </Typography.Text>
        ) : null}
      </ScrollView>
      <ConversationSheet
        campaignId={campaignId}
        recipient={open}
        onClose={() => setOpen(null)}
        onLead={openLead}
      />
    </View>
  );
}

export const ApdmCampaignDetailPage = wrapNavigatorScreen(
  (props: ApdmCampaignDetailParams) => {
    const profile = useUserProfileInfo();
    const apdm = useStoreFeature(StoreFeature.Apdm);
    if (!apdm.enabled)
      return <FeatureNotEnabledView feature={StoreFeature.Apdm} />;
    if (!profile?.user.enabled)
      return <Text>当前门店尚未开通私域营销，或账号不可用。</Text>;
    if (!props.campaignId) return <Text>没有找到这个活动。</Text>;
    return (
      <CampaignDetail
        key={`${profile.store.id}:${props.campaignId}`}
        storeId={profile.store.id}
        campaignId={props.campaignId}
        filter={props.filter}
        initialTab={props.tab}
      />
    );
  },
  { title: '活动详情', headerShown: false, headerMode: 'float' },
);
